import { axiosInstance } from '@share/request/axios'
import { AxiosResponse } from 'axios'
import { isFunction } from './is'
import { ELimitFreeType } from '@root/packages/m/src/constant/common'
import { getCookieDomainByHost } from '@share/common/siteConfig'

// 设置Cookie
export const setCookie = (
  name: string,
  value: string | number,
  domain: string,
  path: string,
  maxAge?: number,
): void => {
  let _expires = new Date().getTime() + 60 * 60 * 24 * 1000
  if (maxAge) {
    _expires = new Date().getTime() + maxAge
  }
  const tempcookie = `${name}=${encodeURIComponent(value)}${
    maxAge ? `; expires=${new Date(_expires).toUTCString()}` : ''
  }${path ? `; path=${path}` : ''}${domain ? `; domain=${domain}` : ''}`
  // Ensure the cookie's size is under the limitation
  if (tempcookie.length < 4096) {
    document.cookie = tempcookie
  }
}

// 获取Cookies
export const getCookie = (key: string, cookies?: string): string => {
  if (import.meta.env.SSR && !cookies) {
    return ''
  }
  const cookiesString = cookies || document.cookie
  const cookieMatch = RegExp(`${key}=([^;]+)`).exec(cookiesString)
  return cookieMatch ? decodeURIComponent(cookieMatch[1]) : ''
}

/**
 * 未登录状态，登录成功拉取用户信息
 */
export const getUserMsg = async (
  url: string,
): Promise<AxiosResponse | null> => {
  if (!url) return null
  try {
    const res = await axiosInstance.get(url)
    return res
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(error)
    return null
  }
}

// beacon方式发送
export const sendBeacon = (url: string, useImage?: boolean): void => {
  // eslint-disable-next-line @typescript-eslint/unbound-method
  if (isFunction(window.navigator.sendBeacon) && useImage !== true) {
    window.navigator.sendBeacon(url)
  } else {
    const i = new Image()
    const cb = (): void => {
      // do nothing
    }
    i.onload = cb
    i.onerror = cb
    i.onabort = cb
    i.src = url
  }
}
/**
 * @description: 异步添加js
 */
export const asyncLoaderScript = (src: string): Promise<unknown> => {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.onload = resolve
    script.onerror = reject
    script.src = src
    if (document.head.append) {
      document.head.append(script)
    } else {
      document.getElementsByTagName('head')[0].appendChild(script)
    }
  })
}

/**
 * obj key value 取反
 * @param obj
 * @returns
 */
export interface IMap<T> {
  [key: string]: T
}

export const getFlippedMap = (obj: IMap<string>): IMap<string> => {
  const flipped = Object.entries(obj).map(([key, value]) => [value, key])
  return Object.fromEntries(flipped) as IMap<string>
}

// 获取书类型 -- 阅读时长上报使用
export const getBookCurChapterType = (
  isLogin: boolean | number,
  limitFree: number,
  vipStatus: number,
  freeStatus: number,
  isBuy: number,
): string => {
  if (limitFree === 1) {
    return '3'
  }
  if (vipStatus === 1) {
    if ((isLogin && freeStatus === 0) || (!isLogin && isBuy === 1)) {
      return '2'
    }
    return '0'
  }
  return '1'
}

/**
 * 获取章节是否可以新人限免
 * @param limitFreeType 书籍的新人限免状态
 * @param updTimes 章节更新时间戳
 */
export const getChapterNewUserLimitFree = (
  limitFreeType: ELimitFreeType,
  updTimes: number,
): boolean => {
  const timeDiff =
    Date.now() -
    (updTimes && String(updTimes).length <= 10 ? updTimes * 1000 : updTimes)
  switch (limitFreeType) {
    case ELimitFreeType.free_before_7_d:
      return updTimes > 7 * 24 * 60 * 60 * 1000
    case ELimitFreeType.free_before_48_h:
      return timeDiff > 2 * 24 * 60 * 60 * 1000
    default:
      return false
  }
}

export const getQueryVariable = (variable: string): string => {
  const query = window.location.search.substring(1)
  const vars = query.split('&')
  for (let i = 0; i < vars.length; i++) {
    const pair = vars[i].split('=')
    if (pair[0] === variable) {
      return pair[1]
    }
  }
  return ''
}

const getStr = (num: number): string => {
  const str = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
  return Array(num)
    .fill('')
    .map((_) => str[Math.floor(Math.random() * str.length)])
    .join('')
}

export const getCsrfToken = (_hostIsMM?: boolean): string => {
  let csrfToken = getCookie('_csrfToken') || ''
  if (!csrfToken) {
    csrfToken = getStr(40)

    setCookie(
      '_csrfToken',
      csrfToken,
      // 矩阵站：根据当前请求 host 动态解析 cookieDomain，
      // 不再针对 .qidian.com / .qdmm.com 做分支硬编码。
      getCookieDomainByHost(),
      '/',
    )
  }
  return csrfToken
}

export function checkIsBaiduSpider(ua: string, ip?: string): boolean {
  const sneakyIPsRegexs = [
    /^220\.181\.\d{1,3}\.\d{1,3}$/,
    /^116\.179\.\d{1,3}\.\d{1,3}$/,
  ]

  const isHitUA = /(baiduspider)/i.test(ua)
  let isHitIP = false
  if (typeof ip === 'string') {
    // https://developer.mozilla.org/zh-CN/docs/Web/HTTP/Headers/X-Forwarded-For
    const ipArr = ip.split(',')
    if (ipArr[0]) {
      const clientIp = ipArr[0].trim()
      isHitIP = sneakyIPsRegexs.some((regex) => regex.test(clientIp))
    }
  }
  return isHitUA || isHitIP
}

export function checkIsSpider(ua: string, ip?: string): boolean {
  const sneakyIPsRegexs = [
    /^220\.181\.\d{1,3}\.\d{1,3}$/,
    /^116\.179\.\d{1,3}\.\d{1,3}$/,
  ]

  const isHitUA = /(bot|spider|crawl|slurp|archiver|facebook|googlebot|bingbot|yandexbot|duckduckgo|twitterbot)/i.test(
    ua,
  )
  let isHitIP = false

  if (typeof ip === 'string') {
    // https://developer.mozilla.org/zh-CN/docs/Web/HTTP/Headers/X-Forwarded-For
    const ipArr = ip.split(',')
    if (ipArr[0]) {
      const clientIp = ipArr[0].trim()
      isHitIP = sneakyIPsRegexs.some((regex) => regex.test(clientIp))
    }
  }

  return isHitUA || isHitIP
}

/**
 * HTML 实体编码（与起点详情页/阅读页 SEO 描述生成规则一致）。
 * - 转义 `< > ' " = \``，以便把任意文本嵌入 HTML 属性 / 内容时不破坏结构；
 * - 不转义 `&`：因为业务侧已存在 `&nbsp;` 等实体，再转义 `&` 会出现 `&amp;nbsp;` 双重编码。
 */
export function htmlEncode(str: string): string {
  return (
    `${str}`
      // .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/'/g, '&#39;')
      .replace(/"/g, '&quot;')
      .replace(/[=]/g, '&#61;')
      .replace(/`/g, '&#96;')
  )
}

// 转换表情 [fn=1] => <i class="icon-emoji w-22px h-22px icon-emoji-1"></i> 1-64都是合法字符
const escapeHtmlMap: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
}
const escapeHtml = (str: string): string => {
  return str.replace(/[&<>"']/g, (s) => {
    return escapeHtmlMap[s] || s
  })
}

export const convertEmoji = (text: string): string => {
  if (!text) return ''
  const escaped = escapeHtml(text)
  return escaped.replace(/\[fn=(\d{1,2})\]/g, (_, num: number) => {
    const n = Number(num)
    if (n >= 1 && n <= 64) {
      return `<i class="icon-emoji w-22px h-22px icon-emoji-${n}"></i>`
    }
    return `[fn=${num}]`
  })
}
